export const PAPI_PAYMENT_ORIGIN = "https://payment-form.papi.mg";
export const KAJIKAJY_PAYMENT_RETURN_TYPE = "KAJIKAJY_PAYMENT_RETURN";
export const PENDING_PAYMENT_STORAGE_PREFIX = "kajikajy-pending-payment";
export function mapInvoiceStatusToPaymentPhase(status) {
    if (status === "paid") {
        return "succeeded";
    }
    if (status === "failed" || status === "cancelled" || status === "expired") {
        return "failed";
    }
    return "verifying";
}
export function isPapiPaymentStatusMessage(event, expectedOrigin = PAPI_PAYMENT_ORIGIN) {
    return (event.origin === expectedOrigin &&
        Boolean(event.data) &&
        typeof event.data === "object" &&
        event.data.type === "PAYMENT_STATUS");
}
export function isKajikajyPaymentReturnMessage(event) {
    return (Boolean(event.data) &&
        typeof event.data === "object" &&
        event.data.type === KAJIKAJY_PAYMENT_RETURN_TYPE &&
        (event.data.status === "success" || event.data.status === "failure"));
}
export function isAllowedKajikajyPaymentOrigin(origin) {
    try {
        const url = new URL(origin);
        if (url.protocol === "https:" &&
            (url.hostname === "kajikajy.com" || url.hostname.endsWith(".kajikajy.com"))) {
            return true;
        }
        return ((url.hostname === "localhost" ||
            url.hostname === "127.0.0.1" ||
            url.hostname === "::1" ||
            url.hostname === "[::1]") &&
            (url.protocol === "http:" || url.protocol === "https:"));
    }
    catch {
        return false;
    }
}
export function parsePaymentReturnQuery(search) {
    const params = new URLSearchParams(search);
    const status = params.get("payment");
    if (status !== "success" && status !== "failure") {
        return null;
    }
    const returnApp = params.get("returnApp");
    return {
        status,
        invoiceId: params.get("invoiceId"),
        paymentIntentId: params.get("paymentIntentId"),
        returnApp: returnApp === "account" || returnApp === "subscribe"
            ? returnApp
            : null
    };
}
export function removePaymentReturnQuery(url) {
    const cleaned = new URL(url.toString());
    cleaned.searchParams.delete("payment");
    cleaned.searchParams.delete("invoiceId");
    cleaned.searchParams.delete("paymentIntentId");
    cleaned.searchParams.delete("returnApp");
    return `${cleaned.pathname}${cleaned.search}${cleaned.hash}`;
}
export function getPendingPaymentStorageKey(source) {
    return `${PENDING_PAYMENT_STORAGE_PREFIX}:${source}`;
}
function isPapiCheckoutUrl(value) {
    if (typeof value !== "string") {
        return false;
    }
    try {
        return new URL(value).origin === PAPI_PAYMENT_ORIGIN;
    }
    catch {
        return false;
    }
}
function isPendingPayment(value, source) {
    if (!value || typeof value !== "object") {
        return false;
    }
    const candidate = value;
    const hasValidTarget = source === "subscribe"
        ? Boolean(candidate.invoiceId) && !candidate.paymentIntentId
        : Boolean(candidate.paymentIntentId) &&
            typeof candidate.statusToken === "string" &&
            candidate.statusToken.length > 0;
    const hasValidCheckoutUrl = candidate.checkoutUrl === undefined ||
        isPapiCheckoutUrl(candidate.checkoutUrl);
    const expiresAtMs = typeof candidate.expiresAt === "string"
        ? Date.parse(candidate.expiresAt)
        : Number.NaN;
    const createdAtMs = typeof candidate.createdAt === "string"
        ? Date.parse(candidate.createdAt)
        : Number.NaN;
    const hasValidDismissedAt = candidate.dismissedAt === undefined ||
        (typeof candidate.dismissedAt === "string" &&
            Number.isFinite(Date.parse(candidate.dismissedAt)));
    return (candidate.version === 1 &&
        candidate.source === source &&
        hasValidTarget &&
        hasValidCheckoutUrl &&
        typeof candidate.paymentReference === "string" &&
        candidate.paymentReference.length > 0 &&
        hasValidDismissedAt &&
        Number.isFinite(expiresAtMs) &&
        Number.isFinite(createdAtMs));
}
function safelyRemoveStorageItem(storage, key) {
    try {
        storage.removeItem(key);
    }
    catch {
        // Storage can be unavailable in hardened or private browser contexts.
    }
}
export function readPendingPayment(storage, source) {
    const key = getPendingPaymentStorageKey(source);
    try {
        const raw = storage.getItem(key);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!isPendingPayment(parsed, source)) {
            safelyRemoveStorageItem(storage, key);
            return null;
        }
        if (Date.parse(parsed.expiresAt) <= Date.now()) {
            safelyRemoveStorageItem(storage, key);
            return null;
        }
        return parsed;
    }
    catch {
        safelyRemoveStorageItem(storage, key);
        return null;
    }
}
export function persistPendingPayment(storage, payment) {
    try {
        storage.setItem(getPendingPaymentStorageKey(payment.source), JSON.stringify(payment));
        return true;
    }
    catch {
        // The popup flow can continue in memory, but same-tab navigation must not.
        return false;
    }
}
export function clearPendingPayment(storage, source) {
    safelyRemoveStorageItem(storage, getPendingPaymentStorageKey(source));
}
export function navigateToPaymentCheckout(checkoutUrl) {
    const topWindow = window.top;
    if (topWindow && topWindow !== window) {
        try {
            topWindow.location.href = checkoutUrl;
            return;
        }
        catch {
            // Sandboxed frames can reject top-level navigation.
        }
    }
    window.location.assign(checkoutUrl);
}
export function openCenteredPaymentPopup(checkoutUrl, name, width = 500, height = 700) {
    const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
    const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
    return window.open(checkoutUrl, name, `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
}
export function relayPaymentReturnToOpener(opener, status, localOrigin) {
    const signal = {
        type: KAJIKAJY_PAYMENT_RETURN_TYPE,
        status
    };
    const origins = new Set([
        "https://souscrire.kajikajy.com",
        "https://compte.kajikajy.com"
    ]);
    if (localOrigin && isAllowedKajikajyPaymentOrigin(localOrigin)) {
        origins.add(localOrigin);
        const localUrl = new URL(localOrigin);
        if (localUrl.hostname === "localhost" ||
            localUrl.hostname === "127.0.0.1" ||
            localUrl.hostname === "::1" ||
            localUrl.hostname === "[::1]") {
            for (const port of ["5174", "5175"]) {
                const developmentOrigin = new URL(localOrigin);
                developmentOrigin.port = port;
                origins.add(developmentOrigin.origin);
            }
        }
    }
    for (const origin of origins) {
        opener.postMessage(signal, origin);
    }
}
//# sourceMappingURL=payment-flow.js.map